import { FilingStatus } from '@/apis/family_application/requests'
import { AppealsStatus } from '@/apis/user_appeals/appeals.schema.ts'
import { ColorType } from '@/common/components/Badge.tsx'
import axios, { AxiosError } from 'axios'
import { ClassValue } from 'class-variance-authority/types'
import clsx from 'clsx'
import { twMerge } from 'tailwind-merge'
import { z, ZodError } from 'zod'
import {
  REGISTERED_SUBDOMAINS,
  RegisteredSubdomain,
  static_errors,
} from './constants.ts'
import { instanceOfError, RequestError } from './types_and_interfaces.ts'
import * as Sentry from '@sentry/react'

export const parseRequestError = (
  error: unknown | AxiosError | ZodError | Error
): RequestError => {
  if (error instanceof ZodError) {
    Sentry.captureException(error)

    if (process.env.NODE_ENV === 'development') {
      console.error(`🛑🛑🛑 Zod error detected: ${error} 🛑🛑🛑`)
    }

    return {
      code: -2,
      message: static_errors.vital_parsing_error,
      details: null,
    }
  }

  if (axios.isAxiosError(error)) {
    const response = error.response
    if (response && instanceOfError(response.data)) {
      return response.data.error
    }
    if (response && response.data?.message) {
      return {
        code: -1,
        message: response.data.message,
        details: null,
      }
    }

    return {
      code: -1,
      message: static_errors.unexpected_contact_support,
      details: null,
    }
  } else {
    return {
      code: -1,
      message: static_errors.unexpected_contact_support,
      details: null,
    }
  }
}

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs))
}

export const getSemanticColor = (
  status: 'Active' | 'Pending' | 'Inactive'
): ColorType => {
  if (status == 'Active') return 'Positive'
  if (status == 'Pending') return 'Notice'
  return 'Negative'
}

export function mockAPI<ResponseType>(
  returnData: ResponseType,
  milliseconds: number = 1500
) {
  return new Promise<ResponseType>((resolve) =>
    setTimeout(() => resolve(returnData), milliseconds)
  )
}

export const getPageData = <T>(
  list: Array<T>,
  itemsPerPage: number,
  currentPage: number
): T[] => {
  const startIndex = (currentPage - 1) * itemsPerPage
  return list.slice(startIndex, startIndex + itemsPerPage)
}

export function getSubdomain(): RegisteredSubdomain | null {
  const host = window.location.hostname
  const hostParts = host.split('.')

  // Check to ensure we have at least a domain and a TLD
  if (hostParts.length < 3) {
    return null // No subdomain present
  }

  const subDomain = hostParts[0]
  if ((REGISTERED_SUBDOMAINS as readonly string[]).includes(subDomain)) {
    return subDomain as RegisteredSubdomain
  }
  return null
}

export const getPaginatedSchema = <T extends z.ZodType>(dataSchema: T) => {
  return z.object({
    data: z.array(dataSchema),
    links: z.object({
      first: z.string(),
      last: z.string(),
      prev: z.null(),
      next: z.null(),
    }),
    meta: z.object({
      current_page: z.number(),
      from: z.number(),
      last_page: z.number(),
      links: z.array(
        z.union([
          z.object({ url: z.null(), label: z.string(), active: z.boolean() }),
          z.object({ url: z.string(), label: z.string(), active: z.boolean() }),
        ])
      ),
      path: z.string(),
      per_page: z.number(),
      to: z.number(),
      total: z.number(),
    }),
  })
}

export const sleep = (seconds: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, seconds * 1000)
  })
}

/**
 * @deprecated Since v2.1.1 - Do not use
 * @param householdSize
 */
export const calculateIncome = (householdSize: number) => {
  const povertyGuidelines: Record<number, number> = {
    1: 15060,
    2: 20440,
    3: 25820,
    4: 31200,
    5: 36580,
    6: 41960,
    7: 47340,
    8: 52720,
  }

  const povertyGuideline =
    povertyGuidelines[householdSize as keyof typeof povertyGuidelines] ||
    (householdSize > 8 ? povertyGuidelines[8] + (householdSize - 8) * 5380 : 0)

  return povertyGuideline * 5.55
}

export const getAppealsSemanticColor = (status: AppealsStatus): ColorType => {
  switch (status) {
    case 'Pending Review':
      return 'Informative'
    case 'Under Review':
      return 'Informative'
    case 'Awaiting Documentation':
      return 'Notice'
    case 'Approved':
      return 'Positive'
    case 'Denied':
      return 'Negative'
    case 'Resolved':
      return 'Positive'
    case 'Incomplete':
      return 'Notice'
    case 'Withdraw':
      return 'Neutral'
    case 'Escalated':
      return 'Negative'
    case 'Closed':
      return 'Neutral'
    default:
      return 'Transparent'
  }
}

export function formatPhoneNumber(phoneNumberString: string): string {
  const cleaned = phoneNumberString.replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)

  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`
  }

  return phoneNumberString
}

export const FILING_OPTIONS: {
  value: FilingStatus
  label: string
}[] = [
  {
    label: 'filed_single',
    value: 'single',
  },
  {
    label: 'filed_jointly',
    value: 'jointly',
  },
  {
    label: 'filed_individually',
    value: 'individually',
  },
  {
    label: 'did_not_file',
    value: 'did_not_file',
  },
] as const

export function removeNonDigitsFromNumber(number: string) {
  return number.replace(/\D/g, '')
}

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  style: 'currency',
})

export function capitalizeAndRemoveUnderscores(text: string | null): string {
  if (!text) {
    return ''
  }

  return text
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}
